import { useEffect, useMemo, useState } from 'react'
import {
  ContactPaymentInstruments,
  RelationshipType,
} from 'types/contacts'
import {
  BeneficiaryEntityType,
} from 'types/beneficiaries'
import { useWalletContactInstrumentsInfiniteQuery } from 'hooks/queries/useWalletContactInstrumentsInfiniteQuery'

interface WalletCapabilities {
  supportsWithdrawals: boolean
  supportsPay: boolean
  isB2cPayoutEnabled: boolean
}

export interface ContactBankAccountFilters {
  relationshipType?: RelationshipType
  currency?: string
  entityType?: BeneficiaryEntityType
  paymentMethods?: string[]
  searchTerm?: string
}

interface GetContactBankAccountsProps {
  walletLsid: string
  filters: ContactBankAccountFilters
  capabilities: WalletCapabilities
}

export const useContactBankAccounts = ({
  walletLsid,
  filters,
  capabilities,
}: GetContactBankAccountsProps) => {
  const { searchTerm, relationshipType, entityType } = filters || {}
  const [contactBankAccounts, setContactBankAccounts] = useState<
    ContactPaymentInstruments[] | undefined
  >()

  const {
    data: contactInstruments,
    fetchNextPage,
    hasNextPage,
    isError,
    isPending,
    isFetchingNextPage,
  } = useWalletContactInstrumentsInfiniteQuery({
    walletLsid: walletLsid,
    size: 100,
    searchTerm,
    enabled: !!walletLsid,
    relationshipType,
    entityType,
  })

  useEffect(() => {
    if (!contactInstruments?.length) {
      setContactBankAccounts([])
      return
    }

    setContactBankAccounts(contactInstruments)
  }, [contactInstruments, filters, capabilities])

  return useMemo(() => {
    return {
      contactBankAccounts,
      fetchNextPage,
      hasNextPage,
      isFetching: isPending || isFetchingNextPage,
      isFetchingNextPage,
      isError,
    }
  }, [
    contactBankAccounts,
    fetchNextPage,
    hasNextPage,
    isPending,
    isFetchingNextPage,
    isError,
  ])
}
